import type { AppState, GrocerySection, ShoppingItem } from '../types';
import { buildShoppingList } from './shopping';

export type GroceryJson = {
  schema: 'lunchbook-grocery-v1';
  week: string;
  generated_at: string;
  meals: { date: string; meal_type: string; recipe: { id: string; name: string } }[];
  ingredients_by_section: Partial<Record<GrocerySection, GroceryJsonItem[]>>;
  safety_modifications: { ingredient: string; modification: string }[];
  bulk_candidates: string[];
};

export type GroceryJsonItem = {
  name: string;
  quantity_hint: string;
  used_in_recipes: string[];
  safety_modification?: string;
  bulk_candidate?: boolean;
  checked?: boolean;
};

const SECTION_LABEL: Record<GrocerySection, string> = {
  produce: 'Produce',
  dairy: 'Dairy',
  bakery: 'Bakery',
  meat: 'Meat',
  pantry: 'Pantry',
  frozen: 'Frozen',
  other: 'Other',
};

function itemsForWeek(state: AppState, weekId: string): ShoppingItem[] {
  const week = state.weeks.find(w => w.id === weekId);
  // Prefer the existing list (with check-offs preserved); else build fresh
  if (week?.shopping_list?.length) return week.shopping_list;
  return buildShoppingList(state, weekId);
}

export function groceryJson(state: AppState, weekId: string, nowIso: string): GroceryJson {
  const week = state.weeks.find(w => w.id === weekId);
  const items = itemsForWeek(state, weekId);
  const bySection: Partial<Record<GrocerySection, GroceryJsonItem[]>> = {};
  for (const it of items) {
    const arr = bySection[it.grocery_section] ?? [];
    arr.push({
      name: it.name,
      quantity_hint: it.quantity_hint,
      used_in_recipes: it.used_in_recipes,
      ...(it.safety_modification ? { safety_modification: it.safety_modification } : {}),
      ...(it.bulk_candidate ? { bulk_candidate: true } : {}),
      ...(it.checked ? { checked: true } : {}),
    });
    bySection[it.grocery_section] = arr;
  }
  const safety = items
    .filter(i => i.safety_modification)
    .map(i => ({ ingredient: i.name, modification: i.safety_modification! }));
  const meals = (week?.meals ?? [])
    .filter(m => m.recipe_id)
    .map(m => {
      const r = state.recipes.find(x => x.id === m.recipe_id)!;
      return { date: m.date, meal_type: m.meal_type, recipe: { id: r.id, name: r.name } };
    });
  const bulk = items.filter(i => i.bulk_candidate).map(i => i.name);
  return {
    schema: 'lunchbook-grocery-v1',
    week: weekId,
    generated_at: nowIso,
    meals,
    ingredients_by_section: bySection,
    safety_modifications: safety,
    bulk_candidates: bulk,
  };
}

export function groceryMarkdown(state: AppState, weekId: string, nowIso: string): string {
  const week = state.weeks.find(w => w.id === weekId);
  const items = itemsForWeek(state, weekId);
  const lines: string[] = [];
  lines.push(`# Lunch & Dinner — week of ${weekId}`);
  lines.push(`*Generated ${nowIso} · ${week?.meals.filter(m => m.recipe_id).length ?? 0} meals planned*`);
  lines.push('');
  const sections: GrocerySection[] = ['produce', 'dairy', 'bakery', 'meat', 'pantry', 'frozen', 'other'];
  for (const sec of sections) {
    const subset = items.filter(i => i.grocery_section === sec);
    if (subset.length === 0) continue;
    lines.push(`## ${SECTION_LABEL[sec]}`);
    for (const it of subset) {
      const safety = it.safety_modification ? ` — ${it.safety_modification} ⚠` : '';
      const bulk = it.bulk_candidate ? ' (bulk candidate)' : '';
      const qty = it.quantity_hint ? ` — ${it.quantity_hint}` : '';
      lines.push(`- ${it.name}${qty}${safety}${bulk}`);
    }
    lines.push('');
  }
  const safety = items.filter(i => i.safety_modification);
  if (safety.length) {
    lines.push(`### Safety modifications`);
    for (const it of safety) lines.push(`- **${it.name}**: ${it.safety_modification}`);
    lines.push('');
  }
  if (week?.meals.length) {
    lines.push(`### Recipes in this list`);
    for (const m of week.meals) {
      if (!m.recipe_id) continue;
      const r = state.recipes.find(x => x.id === m.recipe_id);
      if (!r) continue;
      lines.push(`- ${m.date} · ${m.meal_type} · ${r.name}`);
    }
    lines.push('');
  }
  const bulk = items.filter(i => i.bulk_candidate);
  if (bulk.length) {
    lines.push(`### Bulk-buy candidates`);
    for (const it of bulk) lines.push(`- ${it.name}`);
  }
  return lines.join('\n');
}
